import axios, { type AxiosError } from 'axios';
import { useMsalAuth } from './useMsalAuth';
import { getBatchBaseUrl, getBatchOrigin } from '../config/trapi';
import type { BatchEnvironment } from '../config/trapi';

interface BatchApiConfig {
  selectedApiPath: string;
  selectedApiVersion: string;
  selectedEnvironment: BatchEnvironment;
}

const describeError = (error: unknown) => {
  const axiosError: AxiosError | null = axios.isAxiosError(error) ? error : null;

  return {
    message: error instanceof Error ? error.message : 'Unknown error',
    code: axiosError?.code ?? 'Not an Axios error',
    status: axiosError?.response?.status,
    statusText: axiosError?.response?.statusText,
    hasRequest: axiosError ? !!axiosError.request : false,
    hasResponse: axiosError ? !!axiosError.response : false,
    method: axiosError?.config?.method,
    url: axiosError?.config?.url,
  };
};

export type ConnectivityResult =
  | { success: true; message: string }
  | { success: false; error: string; details?: unknown };

export function useBatchApi() {
  const { getToken, getUserOid } = useMsalAuth();

  const buildTRAPIHeaders = async (apiPath: string) => {
    if (!apiPath) {
      throw new Error('API path is required to build TRAPI headers.');
    }

    const token = await getToken();
    const trimmedPath = apiPath.trim();
    const normalizedPath = trimmedPath.startsWith('/') ? trimmedPath : `/${trimmedPath}`;

    const headers: Record<string, string> = {
      Authorization: `Bearer ${token}`,
      'x-trapi-api-path': normalizedPath,
    };

    try {
      const userOid = await getUserOid();
      if (userOid) {
        headers['x-ms-user-oid'] = userOid;
      }
    } catch (error: unknown) {
      console.warn('Failed to resolve user OID for header:', error);
    }

    return headers;
  };

  const testConnectivity = async (environment: BatchEnvironment): Promise<ConnectivityResult> => {
    console.log('Starting comprehensive connectivity test...', { environment });
    const origin = getBatchOrigin(environment);

    try {
      console.log(`Test 1: Connectivity to ${origin}...`);
      await fetch(origin, {
        method: 'GET',
        mode: 'no-cors',
        cache: 'no-cache',
      });
      console.log('Basic connectivity: SUCCESS');
    } catch (error: unknown) {
      console.error('Basic connectivity: FAILED', error);
      return { success: false, error: `Cannot reach ${origin}`, details: error };
    }

    try {
      const baseUrl = getBatchBaseUrl(environment);
      console.log(`Test 2: Testing batch endpoint accessibility at ${baseUrl}/health...`);
      const token = await getToken();
      const testResponse = await axios.get(`${baseUrl}/health`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        timeout: 10000,
        validateStatus: () => true,
      });
      console.log('Batch endpoint response:', testResponse.status, testResponse.statusText);

      if (testResponse.status >= 200 && testResponse.status < 500) {
        return { success: true, message: 'TRAPI service is accessible' };
      }

      return {
        success: false,
        error: `Batch endpoint returned ${testResponse.status}`,
        details: testResponse,
      };
    } catch (error: unknown) {
    console.error('Batch endpoint test: FAILED', describeError(error));

    const axiosError: AxiosError | null = axios.isAxiosError(error) ? error : null;

      if (axiosError) {
        if (axiosError.code === 'ECONNABORTED') {
          return {
            success: false,
            error: 'Connection timeout',
            details: 'The connection timed out. This usually indicates network connectivity issues.',
          };
        }
        if (axiosError.code === 'NETWORK_ERROR' || axiosError.message === 'Network Error') {
          return {
            success: false,
            error: 'Network error',
            details: 'Cannot establish connection to TRAPI service. Check firewall/proxy settings.',
          };
        }
        if (axiosError.response?.status === 0) {
          return {
            success: false,
            error: 'CORS or network blocked',
            details: 'Request blocked by CORS policy or network restrictions.',
          };
        }
      }

      return { success: false, error: 'Connection failed', details: error };
    }
  };

  const runConnectivityTest = async (environment: BatchEnvironment) => {
    console.log('Manual connectivity test initiated...', { environment });
    const result = await testConnectivity(environment);
    console.log('Manual test result:', result);
    return result;
  };

  const uploadFile = async (file: File, config: BatchApiConfig): Promise<string> => {
    const baseUrl = getBatchBaseUrl(config.selectedEnvironment);

    try {
      const formData = new FormData();
      formData.append('file', file, file.name);
      formData.append('purpose', 'batch');

      const headers = await buildTRAPIHeaders(config.selectedApiPath);

      console.log('Uploading file:', file.name);
      console.log('Upload URL:', `${baseUrl}/files?api-version=${config.selectedApiVersion}`);
      console.log('Selected API Path:', config.selectedApiPath);
      console.log('Selected API Version:', config.selectedApiVersion);
      console.log('Selected Environment:', config.selectedEnvironment);

      const connectivityResult = await testConnectivity(config.selectedEnvironment);
      console.log('TRAPI connectivity test:', connectivityResult);

      if (!connectivityResult.success) {
        throw new Error(
          `Connectivity check failed: ${connectivityResult.error}\n\nDetails: ${String(connectivityResult.details ?? '')}\n\nPlease check:\n• Your internet connection\n• Corporate firewall settings\n• VPN configuration\n• Contact IT support if on corporate network`,
        );
      }

      const response = await axios.post(
        `${baseUrl}/files?api-version=${config.selectedApiVersion}`,
        formData,
        {
          headers: {
            ...headers,
            Accept: 'application/json',
          },
          timeout: 120000,
        },
      );

      if (response.status !== 200 && response.status !== 201) {
        throw new Error(`File upload failed with status: ${response.status}. Response: ${JSON.stringify(response.data)}`);
      }

      console.log('File upload response:', response.data);
      return response.data.input_file_id;
    } catch (error: unknown) {
      const axiosError: AxiosError | null = axios.isAxiosError(error) ? error : null;

      console.error('File upload error details:', describeError(error));

      if (axiosError) {
        const baseUrl = getBatchBaseUrl(config.selectedEnvironment);

        if (axiosError.code === 'ECONNABORTED') {
          throw new Error('File upload timed out. Please try again with a smaller file or check your network connection.');
        }
        if (axiosError.code === 'NETWORK_ERROR' || axiosError.message === 'Network Error') {
          console.error('Network error details:', {
            url: `${baseUrl}/files?api-version=${config.selectedApiVersion}`,
            apiPath: config.selectedApiPath,
            environment: config.selectedEnvironment,
          });
          throw new Error(
            `Network error during file upload to: ${baseUrl}/files\n\nPossible causes:\n• CORS policy blocking cross-origin request\n• Corporate firewall or proxy settings\n• VPN blocking the connection\n• TRAPI service is down\n• Authentication token expired\n\nTry:\n• Refresh the page to get a new token\n• Check if you can access ${baseUrl} directly in your browser\n• Contact IT support if using corporate network`,
          );
        }
        if (axiosError.response) {
          console.error('Response error details:', {
            status: axiosError.response.status,
            statusText: axiosError.response.statusText,
            data: axiosError.response.data,
          });
          throw new Error(
            `File upload failed: ${axiosError.response.status} ${axiosError.response.statusText}. ${JSON.stringify(axiosError.response.data)}`,
          );
        }
        if (axiosError.request) {
          console.error('Request error details:', describeError(error));
          throw new Error(
            `No response received from server at ${baseUrl}. This usually indicates:\n• Network connectivity issues\n• Server is unreachable\n• Firewall blocking the request\n\nPlease check your network connection and try again.`,
          );
        }
      }

      throw new Error(`File upload failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  const waitForFileProcessing = async (
    uploadedFileId: string,
    config: BatchApiConfig,
    onStatusUpdate?: (status: string) => void,
  ) => {
    const baseUrl = getBatchBaseUrl(config.selectedEnvironment);
    let status = '';
    const headers = await buildTRAPIHeaders(config.selectedApiPath);

    while (status !== 'processed') {
      onStatusUpdate?.(status || 'unknown');

      const response = await axios.get(
        `${baseUrl}/files/${uploadedFileId}?api-version=${config.selectedApiVersion}`,
        { headers },
      );

      status = response.data.status;

      if (status === 'failed') {
        throw new Error('File processing failed.');
      }

      await new Promise((resolve) => setTimeout(resolve, 15000));
    }
  };

  const submitBatchJob = async (uploadedFileId: string, config: BatchApiConfig): Promise<string> => {
    const baseUrl = getBatchBaseUrl(config.selectedEnvironment);
    const payload = {
      input_file_id: uploadedFileId,
      endpoint: 'chat/completions',
      completion_window: 'PT2H',
      metadata: null,
    };

    const headers = await buildTRAPIHeaders(config.selectedApiPath);
    headers['Content-Type'] = 'application/json';

    console.log('Submitting batch job with payload:', payload);
    console.log('Headers (keys):', Object.keys(headers));
    console.log('Base URL:', baseUrl);

    const response = await axios.post(
      `${baseUrl}/batches?api-version=${config.selectedApiVersion}`,
      payload,
      { headers },
    );

    if (response.status !== 200 && response.status !== 201) {
      throw new Error(`Job submission failed with status: ${response.status}. Response: ${JSON.stringify(response.data)}`);
    }

    return response.data.id;
  };

  const waitForJobCompletion = async (
    batchJobId: string,
    config: BatchApiConfig,
    onStatusUpdate?: (status: string) => void,
  ) => {
    const baseUrl = getBatchBaseUrl(config.selectedEnvironment);
    let status = 'queued';
    const headers = await buildTRAPIHeaders(config.selectedApiPath);
    let waitedMinutes = 0;
    let consecutiveErrors = 0;
    const maxConsecutiveErrors = 3;

    const terminalStatuses = ['succeeded', 'completed', 'failed', 'partially failed', 'partially_failed', 'cancelled'];
    const intermediateStatuses = ['queued', 'validating', 'processing', 'in_progress', 'running', 'finalizing'];

    console.log(`Starting to poll job ${batchJobId} with API path: ${config.selectedApiPath}`);

    while (!terminalStatuses.includes(status)) {
      onStatusUpdate?.(status);

      try {
        console.log(`Polling job ${batchJobId} (${waitedMinutes.toFixed(1)}min) - current status: ${status}`);

        const response = await axios.get(
          `${baseUrl}/batches/${batchJobId}?api-version=${config.selectedApiVersion}`,
          { headers },
        );

        const newStatus = String(response.data.status || '').toLowerCase();
        const statusChanged = newStatus !== status;
        status = newStatus;

        if (statusChanged) {
          console.log(`Job ${batchJobId} status changed to: ${status}`);
        }

        console.log('Full job response:', {
          id: response.data.id,
          status: response.data.status,
          created_at: response.data.created_at,
          completed_at: response.data.completed_at,
          failed_at: response.data.failed_at,
          error: response.data.error,
        });

        consecutiveErrors = 0;

        if (status === 'failed') {
          const errorDetails = response.data.errors || response.data.error || 'No error details provided';
          console.error(`Job ${batchJobId} failed with details:`, errorDetails);
          throw new Error(`Job failed with status: ${status}. Details: ${JSON.stringify(errorDetails)}`);
        }

        if (status === 'cancelled') {
          throw new Error(`Job was cancelled. Status: ${status}`);
        }

        if (status === 'partially failed' || status === 'partially_failed') {
          console.warn(`Job ${batchJobId} completed with partial failures. Successful results and error logs may both be available.`);
        }

        if (!terminalStatuses.includes(status) && !intermediateStatuses.includes(status)) {
          console.warn(`Unknown job status: ${status}. Continuing to poll...`);
        }
      } catch (error: unknown) {
        const axiosError: AxiosError | null = axios.isAxiosError(error) ? error : null;
        if (!axiosError) {
          throw error;
        }

        consecutiveErrors++;
        console.error(
          `API error polling job ${batchJobId} (attempt ${consecutiveErrors}/${maxConsecutiveErrors}):`,
          describeError(error),
        );

        if (axiosError?.response) {
          console.error('Response details:', {
            status: axiosError.response.status,
            statusText: axiosError.response.statusText,
            data: axiosError.response.data,
          });

          if (axiosError.response.status === 404) {
            throw new Error(`Job ${batchJobId} not found. It may have been deleted or expired.`);
          }
          if (axiosError.response.status === 401 || axiosError.response.status === 403) {
            throw new Error('Authentication failed. Please refresh the page and try again.');
          }
        }

        if (consecutiveErrors >= maxConsecutiveErrors) {
          throw new Error(
            `Failed to poll job status after ${maxConsecutiveErrors} consecutive errors. Last error: ${
              error instanceof Error ? error.message : 'Unknown error'
            }`,
          );
        }

        console.log('Waiting 10 seconds before retry due to API error...');
        await new Promise((resolve) => setTimeout(resolve, 10000));
      }

      if (!terminalStatuses.includes(status) && consecutiveErrors === 0) {
        console.log('Waiting 20 seconds before next poll...');
        await new Promise((resolve) => setTimeout(resolve, 20000));
        waitedMinutes += 0.33;
      }
    }

    console.log(`Job ${batchJobId} completed with final status: ${status}`);
    return status;
  };

  const getJobResults = async (
    batchJobId: string,
    config: BatchApiConfig,
    fileName?: string,
  ): Promise<string> => {
    const baseUrl = getBatchBaseUrl(config.selectedEnvironment);
    const headers = await buildTRAPIHeaders(config.selectedApiPath);
    const response = await axios.get(
      `${baseUrl}/files/${batchJobId}/content?api-version=${config.selectedApiVersion}`,
      {
        headers,
        responseType: 'blob',
      },
    );

    const blob = new Blob([response.data], { type: 'application/json' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    const downloadFileName = fileName
      ? `${fileName.replace('.jsonl', '')}_result.jsonl`
      : 'batch_result.jsonl';
    link.download = downloadFileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);

    return downloadFileName;
  };

  const downloadOutputFile = async (
    outputFileId: string,
    config: BatchApiConfig,
    jobId?: string,
    downloadKind: 'result' | 'error' = 'result',
  ): Promise<string> => {
    const baseUrl = getBatchBaseUrl(config.selectedEnvironment);
    const headers = await buildTRAPIHeaders(config.selectedApiPath);
    
    console.log('Downloading output file:', { outputFileId, apiVersion: config.selectedApiVersion });
    
    const response = await axios.get(
      `${baseUrl}/files/${outputFileId}/content?api-version=${config.selectedApiVersion}`,
      {
        headers,
        responseType: 'blob',
      },
    );

    const blob = new Blob([response.data], { type: 'application/json' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    const suffix = downloadKind === 'error' ? 'errors' : 'result';
    const downloadFileName = jobId
      ? `batch_${jobId}_${suffix}.jsonl`
      : `batch_${outputFileId}_${suffix}.jsonl`;
    link.download = downloadFileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);

    console.log('Downloaded file:', downloadFileName);
    return downloadFileName;
  };

  return {
    uploadFile,
    waitForFileProcessing,
    submitBatchJob,
    waitForJobCompletion,
    getJobResults,
    downloadOutputFile,
    testConnectivity,
    runConnectivityTest,
  };
}
